import { useQueryClient } from '@tanstack/react-query'
import to from 'await-to-js'
import CreatableSelect from 'components/FormUtils/CreatableSelect'
import { SelectOption } from 'components/FormUtils/Select'
import { Control, FieldValues, Path, UseFormSetError } from 'react-hook-form'
import useApiService from 'services/api'
import { handleFormError } from 'utils/handleFormError'
import { CompanyTypeSlug } from 'models/companies'
import { MULTI_VALUE_SELECT_COLORS } from 'utils/constants'

export function CreatableCampaignSelector<T extends FieldValues>({
  name,
  control,
  options,
  setError,
  label,
  placeholder,
  className,
  optional,
  labelClassName,
  whiteBackground = false,
  companyType = 'restaurants-bars',
  isClearable = true,
}: {
  name: Path<T>
  control: Control<T, any>
  options?: SelectOption[]
  setError: UseFormSetError<T>
  label?: string
  placeholder?: string
  className?: string
  optional?: boolean
  labelClassName?: string
  whiteBackground?: boolean
  companyType?: CompanyTypeSlug
  isClearable?: boolean
}) {
  const api = useApiService()
  const queryClient = useQueryClient()
  const { data: campaigns } = api.useGetCampaignsOptions({
    typeOrTypeId: companyType,
    requireFilter: true, // Prevent selecting campaigns when company type is unknown
  })

  const onCreateOption = async (value: string) => {
    const [err, res] = await to(
      api.postCampaign({
        name: value,
        type_slug: companyType,
      })
    )

    if (err) handleFormError(err, setError)
    else {
      // Invalidate campaigns query
      queryClient.invalidateQueries({ queryKey: ['campaigns'] })
      return {
        label: res?.name,
        value: res?.id,
        color: res?.color,
      }
    }

    return null
  }

  const getCampaignColor = (campaignValue: any) => {
    const campaign = campaigns?.find((c) => c.value === campaignValue)
    if (campaign?.color) {
      return (
        MULTI_VALUE_SELECT_COLORS.find(
          (c) => c.background === campaign.color
        ) || MULTI_VALUE_SELECT_COLORS[0]
      )
    }
    return MULTI_VALUE_SELECT_COLORS[0]
  }

  const customStyles = {
    ...(whiteBackground && {
      control: (provided: any) => ({
        ...provided,
        backgroundColor: 'white',
      }),
    }),
    multiValue: (provided: any, state: any) => {
      const colors = getCampaignColor(state.data.value)
      return {
        ...provided,
        backgroundColor: colors.background,
        color: colors.text,
      }
    },
    multiValueLabel: (provided: any, state: any) => {
      const color = getCampaignColor(state.data.value)
      return {
        ...provided,
        color: color.text,
      }
    },
    multiValueRemove: (provided: any, state: any) => {
      const color = getCampaignColor(state.data.value)
      return {
        ...provided,
        color: color.text,
        ':hover': {
          backgroundColor: color.text,
          color: color.background,
          borderRadius: '0px 6px 6px 0px',
        },
      }
    },
    option: (provided: any, state: any) => {
      const color = getCampaignColor(state.data.value)
      return {
        ...provided,
        color: color.text,
      }
    },
  }

  return (
    <CreatableSelect
      name={name}
      isMulti
      control={control}
      type="text"
      label={label}
      placeholder={placeholder}
      options={options}
      onCreateOption={onCreateOption}
      className={className}
      optional={optional}
      styles={customStyles}
      labelClassName={labelClassName}
      isClearable={isClearable}
    />
  )
}
